export type MarketCode = "global" | "in" | "ae" | "us" | "uk";
export type FunnelStage = "awareness" | "consideration" | "activation";
export type ComplianceReview = "not-applicable" | "reviewed-boundary" | "requires-local-review";
export type PublicationState = "published" | "early-access" | "gated";

export type Machine3Taxonomy = {
  country: MarketCode;
  role: string;
  industry: string;
  funnelStage: FunnelStage;
  searchIntent: string;
  primaryKeyword: string;
  supportingKeywords: readonly string[];
  productCapability: string;
  proofSource: string;
  complianceReview: ComplianceReview;
  cta: string;
  publicationState: PublicationState;
  lastVerified: string;
  repurposingStatus: "ready" | "country-review-required";
};

export type GlobalGrowthPage = {
  path: string;
  group: "solution" | "role" | "market" | "industry" | "index";
  eyebrow: string;
  title: string;
  description: string;
  thesis: string;
  sections: readonly (readonly [string, string])[];
  links?: readonly (readonly [string, string])[];
  boundary: string;
  ctaLabel: string;
  ctaHref: string;
  taxonomy: Machine3Taxonomy;
};

const verified = "2026-08-30";

function taxonomy(overrides: Partial<Machine3Taxonomy> & Pick<Machine3Taxonomy, "primaryKeyword" | "productCapability" | "cta">): Machine3Taxonomy {
  return {
    country: "global",
    role: "cross-functional",
    industry: "cross-industry",
    funnelStage: "consideration",
    searchIntent: "commercial investigation",
    supportingKeywords: [],
    proofSource: "Verified RAQOZ public capability catalogue and release labels",
    complianceReview: "not-applicable",
    publicationState: "published",
    lastVerified: verified,
    repurposingStatus: "ready",
    ...overrides,
  };
}

export const globalGrowthPages: readonly GlobalGrowthPage[] = [
  {
    path: "/solutions",
    group: "index",
    eyebrow: "GLOBAL SOLUTION LIBRARY",
    title: "Procurement control for the points where buying breaks.",
    description: "Explore purchase approvals, RFQs, quotation comparison, purchase orders, three-way matching, audit evidence and multi-location procurement.",
    thesis: "RAQOZ connects the decision record from request to receipt. Each solution page distinguishes currently released workflow from integration or country capability that remains planned.",
    sections: [
      ["Control before commitment", "Capture the need, route approval and retain the authority behind the commercial commitment."],
      ["Comparable supplier decisions", "Invite suppliers to one requirement, compare accepted offers and preserve why an award was made."],
      ["Evidence after the order", "Connect the controlled order to acknowledgement, receipt, invoice exceptions and reconciliation evidence."],
    ],
    links: [
      ["Purchase approvals", "/solutions/purchase-approvals"],
      ["RFQ management", "/solutions/rfq-management"],
      ["Quotation comparison", "/solutions/quotation-comparison"],
      ["Purchase orders", "/solutions/purchase-orders"],
      ["Three-way matching", "/solutions/three-way-matching"],
      ["Procurement audit trail", "/solutions/procurement-audit-trail"],
      ["Multi-location procurement", "/solutions/multi-location-procurement"],
    ],
    boundary: "Solution pages describe verified RAQOZ workflows. They do not imply a native accounting, banking or country-tax integration where one is not released.",
    ctaLabel: "Run one live purchase",
    ctaHref: "/one-purchase-challenge",
    taxonomy: taxonomy({ primaryKeyword: "procurement solutions", productCapability: "request-to-evidence workflow", cta: "Run one live purchase" }),
  },
  {
    path: "/solutions/purchase-approvals",
    group: "solution",
    eyebrow: "PURCHASE APPROVAL SOFTWARE",
    title: "Route every purchase to the right accountable approver.",
    description: "Purchase approval software for growing organisations that need clear authority, value bands, budget context and an attributable decision.",
    thesis: "Employees should describe what they need; the policy should determine who decides. RAQOZ keeps the request, approval and later purchasing evidence connected.",
    sections: [
      ["Simple for requesters", "Capture purpose, quantity, location, required date and budget context without making ordinary employees learn the authority chart."],
      ["Authority by value and policy", "Approval paths can reflect organisational roles, departments, locations and configured thresholds."],
      ["Visible exceptions", "Emergency, rejected and exception paths remain attributable instead of becoming invisible chat approvals."],
    ],
    links: [["Build an approval matrix", "/tools/procurement-approval-matrix-builder"], ["Download the matrix", "/templates/procurement-approval-matrix"]],
    boundary: "RAQOZ records organisational approval workflow; it does not create legal delegation authority on the organisation's behalf.",
    ctaLabel: "Start with one purchase",
    ctaHref: "/one-purchase-challenge",
    taxonomy: taxonomy({ primaryKeyword: "purchase approval software", supportingKeywords: ["purchase approval workflow", "procurement approval matrix"], productCapability: "requisitions and configurable approvals", cta: "Start with one purchase" }),
  },
  {
    path: "/solutions/rfq-management",
    group: "solution",
    eyebrow: "RFQ MANAGEMENT SOFTWARE",
    title: "Give every invited supplier one comparable requirement.",
    description: "RFQ management software that keeps requirements, supplier invitations, clarifications and quotations attached to the same sourcing event.",
    thesis: "An RFQ is useful only when suppliers receive the same commercial requirement and the buyer can reconstruct the response trail.",
    sections: [
      ["One requirement", "Keep scope, quantities, delivery locations, response dates and requested evidence consistent across invited suppliers."],
      ["Attributable participation", "Supplier responses remain connected to the legitimate buyer invitation and responsible supplier user."],
      ["Comparable handoff", "Accepted responses move into technical and commercial evaluation without rebuilding the file in another spreadsheet."],
    ],
    links: [["Create an RFQ free", "/tools/rfq-generator"], ["Download an RFQ template", "/templates/rfq-template"]],
    boundary: "RAQOZ supports legitimate buyer invitations. It is not an indiscriminate supplier lead list or public tendering authority.",
    ctaLabel: "Run one sourcing event",
    ctaHref: "/one-purchase-challenge",
    taxonomy: taxonomy({ primaryKeyword: "RFQ management software", supportingKeywords: ["RFQ software", "request for quotation workflow"], productCapability: "RFQ and supplier invitations", cta: "Run one sourcing event" }),
  },
  {
    path: "/solutions/quotation-comparison",
    group: "solution",
    eyebrow: "VENDOR QUOTATION COMPARISON",
    title: "Compare the evidence—not only the lowest visible price.",
    description: "Compare supplier quotations across technical acceptance, total cost, delivery, warranty and commercial terms with a recorded award rationale.",
    thesis: "A defensible comparison separates mandatory compliance from evaluated commercial value and preserves why the selected supplier was appropriate.",
    sections: [
      ["Normalise scope", "Bring freight, installation, tax treatment, warranty, exclusions and delivery assumptions onto a comparable basis."],
      ["Separate fit from price", "Make technical acceptance visible before a low headline amount is treated as an evaluated result."],
      ["Retain the rationale", "When the selected supplier is not L1, preserve the evidenced quality, delivery, lifecycle-cost or risk reason."],
    ],
    links: [["Compare three quotations", "/tools/vendor-quotation-comparison"], ["Download comparison sheet", "/templates/vendor-comparison-sheet"]],
    boundary: "RAQOZ supports evaluation and evidence. It does not guarantee supplier performance or replace authorised human judgement.",
    ctaLabel: "Compare one real purchase",
    ctaHref: "/one-purchase-challenge",
    taxonomy: taxonomy({ primaryKeyword: "vendor quotation comparison software", supportingKeywords: ["supplier quote comparison", "L1 comparison"], productCapability: "technical and commercial comparison", cta: "Compare one real purchase" }),
  },
  {
    path: "/solutions/purchase-orders",
    group: "solution",
    eyebrow: "PURCHASE ORDER WORKFLOW",
    title: "Turn an authorised award into a controlled order.",
    description: "Purchase order software that carries the approved supplier decision into acknowledgement, receipt and invoice evidence.",
    thesis: "A purchase order should communicate an authorised decision rather than recreate one after the supplier has already been instructed.",
    sections: [
      ["Issue from the award", "Carry selected supplier, lines, quantities, commercial terms and delivery requirements from the authorised decision."],
      ["Keep versions attributable", "Material changes and supplier acknowledgement remain connected to the controlled document history."],
      ["Connect fulfilment", "Partial or full receipt and later invoice evidence refer back to the same order."],
    ],
    links: [["Generate a PO draft", "/tools/purchase-order-generator"], ["Download PO template", "/templates/purchase-order-template"]],
    boundary: "The organisation remains responsible for contractual language, tax treatment and authorised issue of each purchase order.",
    ctaLabel: "Issue one controlled PO",
    ctaHref: "/one-purchase-challenge",
    taxonomy: taxonomy({ primaryKeyword: "purchase order software", supportingKeywords: ["purchase order approval workflow", "PO management"], productCapability: "purchase orders and acknowledgement", cta: "Issue one controlled PO" }),
  },
  {
    path: "/solutions/three-way-matching",
    group: "solution",
    eyebrow: "PO · RECEIPT · INVOICE",
    title: "Make quantity and value exceptions visible before approval.",
    description: "Three-way matching software for comparing the purchase order, accepted receipt and supplier invoice before invoice approval.",
    thesis: "A match is not a decorative green tick. It should show what was ordered, what was accepted and what the supplier is asking to be paid.",
    sections: [
      ["Order", "Use the authorised quantity, rate and commercial terms as the approved baseline."],
      ["Receipt", "Record partial, full or exception receipt against what the organisation actually accepted."],
      ["Invoice", "Surface quantity, rate and value differences for attributable resolution before approval."],
    ],
    links: [["Check a match free", "/tools/three-way-match-checker"], ["Read the exception guide", "/guides/invoice-quantity-exceeds-received"]],
    boundary: "A three-way match is one procurement control. Tax, duplicate invoice, supplier identity and other finance checks may still be required.",
    ctaLabel: "Test one invoice journey",
    ctaHref: "/one-purchase-challenge",
    taxonomy: taxonomy({ primaryKeyword: "three way matching software", supportingKeywords: ["PO receipt invoice matching", "invoice matching"], productCapability: "receipt and invoice matching", cta: "Test one invoice journey" }),
  },
  {
    path: "/solutions/procurement-audit-trail",
    group: "solution",
    eyebrow: "PROCUREMENT AUDIT EVIDENCE",
    title: "Reconstruct why a purchase happened months later.",
    description: "A procurement audit trail connecting request, authority, supplier competition or exception, award, order, receipt, invoice and corrections.",
    thesis: "The valuable record is not a list of clicks. It is the attributable evidence needed to explain the need, decision, commitment and outcome.",
    sections: [
      ["Decision lineage", "Connect the original need to approval, sourcing approach and supplier-selection rationale."],
      ["Immutable events", "Material workflow actions create attributable events; corrections add evidence instead of quietly rewriting history."],
      ["Governed export", "Evidence packs and exports preserve the record while protected source files remain subject to access controls."],
    ],
    links: [["Check evidence completeness", "/tools/procurement-evidence-score"], ["Open the file-note template", "/templates/procurement-file-note"]],
    boundary: "RAQOZ provides records and evidence exports. It does not provide an audit opinion, certification or legal assurance.",
    ctaLabel: "Review one purchase file",
    ctaHref: "/procurement-autopsy",
    taxonomy: taxonomy({ primaryKeyword: "procurement audit trail software", supportingKeywords: ["purchase audit trail", "procurement evidence"], productCapability: "immutable audit events and evidence export", cta: "Review one purchase file" }),
  },
  {
    path: "/solutions/multi-location-procurement",
    group: "solution",
    eyebrow: "MULTI-LOCATION PROCUREMENT",
    title: "Let local teams request while central control stays visible.",
    description: "Multi-location procurement software for branches, clinics, campuses, sites and service locations operating under shared policy.",
    thesis: "Local speed and central governance are not opposites. Locations can describe the operational need while authority, supplier decisions and evidence remain organisation-scoped.",
    sections: [
      ["Local initiation", "The operating location records what is needed, where, why and by when."],
      ["Central governance", "Departments, budgets, approval authority and supplier decisions remain visible to the responsible group functions."],
      ["Outcome by location", "Orders, receipts, assets, obligations and exceptions retain their location context."],
    ],
    links: [["Plan a new location", "/tools/new-location-procurement-planner"], ["Download branch checklist", "/templates/new-branch-procurement-checklist"]],
    boundary: "RAQOZ is a procurement operating layer; it does not replace branch accounting, clinical, property or field-service systems.",
    ctaLabel: "Pilot one location",
    ctaHref: "/one-purchase-challenge",
    taxonomy: taxonomy({ primaryKeyword: "multi location procurement software", supportingKeywords: ["branch purchase control", "multi site purchasing"], productCapability: "organisation-scoped locations and workflows", cta: "Pilot one location" }),
  },
  {
    path: "/roles",
    group: "index",
    eyebrow: "ROLE-BASED ENTRY",
    title: "One purchase record. Different decisions for every role.",
    description: "Explore RAQOZ for founders, finance teams, procurement teams, accountants and advisors.",
    thesis: "RAQOZ remains one horizontal product. Role pages explain the same accountable purchase through the questions each participant must answer.",
    sections: [
      ["Founders", "See commitments, exceptions and supplier decisions without personally chasing each transaction."],
      ["Finance and procurement", "Control authority, comparison, receipt and invoice evidence in the operating workflow."],
      ["Advisors", "Help clients establish a repeatable control environment without becoming the transaction operator."],
    ],
    links: [["Founders", "/roles/founders"], ["Finance teams", "/roles/finance-teams"], ["Procurement teams", "/roles/procurement-teams"], ["Accountants and advisors", "/roles/accountants-and-advisors"]],
    boundary: "Role pages describe operating responsibilities, not professional, accounting, audit or legal advice.",
    ctaLabel: "Run one purchase together",
    ctaHref: "/one-purchase-challenge",
    taxonomy: taxonomy({ primaryKeyword: "procurement software by role", productCapability: "role-based procurement workflow", cta: "Run one purchase together" }),
  },
  {
    path: "/roles/founders",
    group: "role",
    eyebrow: "FOR FOUNDERS & OPERATORS",
    title: "Control purchasing without becoming the approval inbox.",
    description: "Procurement software for founders who need visibility and accountability without personally coordinating every request, supplier and invoice.",
    thesis: "A founder should define authority and see exceptions—not become the human middleware between WhatsApp groups, suppliers and finance.",
    sections: [
      ["Delegate visibly", "Make approval authority explicit and let the workflow route routine purchases to the right accountable person."],
      ["See the decision", "Understand what was requested, which suppliers responded, why one won and what remains unresolved."],
      ["Intervene by exception", "Focus on material value, policy exceptions and risk instead of every routine transaction."],
    ],
    boundary: "RAQOZ supports delegated operating control; company leadership remains responsible for governance and authorised commitments.",
    ctaLabel: "Bring one live purchase",
    ctaHref: "/one-purchase-challenge",
    taxonomy: taxonomy({ role: "founder", primaryKeyword: "procurement software for founders", productCapability: "approval and management visibility", cta: "Bring one live purchase" }),
  },
  {
    path: "/roles/finance-teams",
    group: "role",
    eyebrow: "FOR FINANCE TEAMS",
    title: "See the buying decision before the invoice reaches finance.",
    description: "Procurement software for finance teams connecting budget context, approval, supplier selection, receipt and invoice exceptions.",
    thesis: "Finance should not have to reconstruct the purchase after the commercial commitment has already happened.",
    sections: [
      ["Authority before spend", "Keep approval and budget context attached to the request before the supplier is instructed."],
      ["Receipt before payment", "Show accepted quantity and service evidence before matching and invoice approval."],
      ["Explainable exceptions", "Retain who resolved quantity, rate, tax-treatment or evidence differences and why."],
    ],
    boundary: "RAQOZ records procurement and payment-state evidence; it does not move funds or replace the accounting ledger.",
    ctaLabel: "Test one invoice journey",
    ctaHref: "/one-purchase-challenge",
    taxonomy: taxonomy({ role: "finance", primaryKeyword: "procurement software for finance teams", productCapability: "budget, receipt and invoice evidence", cta: "Test one invoice journey" }),
  },
  {
    path: "/roles/procurement-teams",
    group: "role",
    eyebrow: "FOR PROCUREMENT TEAMS",
    title: "Spend less time rebuilding context and more time improving decisions.",
    description: "Procurement software for managing requests, supplier invitations, quotations, comparison, awards, orders and performance evidence.",
    thesis: "Procurement creates more value when requirements and evidence arrive in one controlled journey rather than being reconstructed across inboxes.",
    sections: [
      ["Structured intake", "Turn a business need into a sourcing-ready requirement without repeated copy-and-paste."],
      ["Comparable sourcing", "Keep supplier invitations, responses, clarifications and award evidence together."],
      ["Outcome learning", "Use receipt, exception, obligation and supplier evidence to inform the next decision."],
    ],
    boundary: "RAQOZ supports professional judgement and workflow; it does not automatically certify suppliers or guarantee sourcing outcomes.",
    ctaLabel: "Run one sourcing event",
    ctaHref: "/one-purchase-challenge",
    taxonomy: taxonomy({ role: "procurement", primaryKeyword: "procurement software for procurement teams", productCapability: "request-to-order sourcing workflow", cta: "Run one sourcing event" }),
  },
  {
    path: "/roles/accountants-and-advisors",
    group: "role",
    eyebrow: "FOR ACCOUNTANTS & ADVISORS",
    title: "Help clients build procurement evidence before year-end reconstruction.",
    description: "Procurement controls for accountants, fractional CFOs, bookkeepers and advisors supporting growing organisations.",
    thesis: "Trusted advisors repeatedly see missing authority, supplier-selection and receipt evidence. RAQOZ can help the client operate the control during the year.",
    sections: [
      ["Assess one purchase", "Use a practical evidence-gap review to identify where the decision record breaks."],
      ["Define workable controls", "Help the client establish approval bands, responsibility and exception paths that people can follow."],
      ["Keep independence clear", "Advisors can recommend controls without making product use an audit conclusion or endorsement."],
    ],
    links: [["Open the advisor controls kit", "/partners/procurement-controls-kit"], ["Check purchase evidence", "/tools/procurement-evidence-score"]],
    boundary: "RAQOZ and its tools do not provide accounting, tax, audit, assurance or legal advice.",
    ctaLabel: "Review the advisor kit",
    ctaHref: "/partners/procurement-controls-kit",
    taxonomy: taxonomy({ role: "accountants and advisors", primaryKeyword: "procurement controls for accountants", supportingKeywords: ["procurement for fractional CFOs", "purchase controls for bookkeepers"], productCapability: "evidence and approval controls", cta: "Review the advisor kit" }),
  },
  {
    path: "/markets",
    group: "index",
    eyebrow: "GLOBAL AUTHORITY · STAGED ACTIVATION",
    title: "Global procurement problems. Country-specific operating context.",
    description: "RAQOZ global procurement guidance with distinct India, UAE, US and UK market pages and truthful early-access boundaries.",
    thesis: "RAQOZ builds global search authority now while country localisation, integrations and sales activation advance only after evidence and review.",
    sections: [
      ["Global core", "Requests, approvals, quotations, orders, receipts and evidence are universal operating problems."],
      ["Local language", "Terminology, advisor ecosystems, tax context and buyer expectations differ by country."],
      ["Staged activation", "Country pages state what is available, what is educational and what still requires localisation."],
    ],
    links: [["India", "/markets/in"], ["United Arab Emirates", "/markets/ae"], ["United States", "/markets/us"], ["United Kingdom", "/markets/uk"]],
    boundary: "Country content is educational and product-oriented. It never implies unverified tax compliance, statutory advice or a released integration.",
    ctaLabel: "Explore your market",
    ctaHref: "/markets/in",
    taxonomy: taxonomy({ primaryKeyword: "global procurement software", productCapability: "global workflow and regional price books", cta: "Explore your market" }),
  },
  {
    path: "/markets/in",
    group: "market",
    eyebrow: "INDIA · LAUNCH MARKET",
    title: "Procurement control for Indian growing businesses.",
    description: "Procurement software for Indian SMEs moving purchase requests, approvals, quotations, POs, receipts and evidence beyond WhatsApp and spreadsheets.",
    thesis: "India is the RAQOZ proving ground, with rupee price books and operational content for businesses using accounting systems such as Tally alongside informal purchasing workflows.",
    sections: [
      ["India-first commercial entry", "Published rupee price books and a free Launch workspace reduce the barrier to structured procurement."],
      ["Useful local context", "Content addresses GST-ready records, MSME payment evidence, CAPEX/OPEX classification and multi-location operations."],
      ["Advisor channel", "Chartered accountants, virtual CFOs, Tally partners and SME consultants can introduce clients to repeatable procurement controls."],
    ],
    links: [["India purchase-order guide", "/guides/purchase-order-format-india"], ["MSME 45-day payment tracker", "/tools/msme-45-day-payment-tracker"], ["Section 43B(h) procurement evidence", "/section-43b-h-procurement"], ["Advisor controls kit", "/partners/procurement-controls-kit"]],
    boundary: "RAQOZ records procurement data and evidence. It does not provide GST, Section 43B(h), MSMED Act, accounting or legal advice.",
    ctaLabel: "Start free in India",
    ctaHref: "/signup?market=in",
    taxonomy: taxonomy({ country: "in", primaryKeyword: "procurement software for Indian SMEs", supportingKeywords: ["purchase approval software India", "GST procurement records"], productCapability: "India price book and country-aware records", cta: "Start free in India" }),
  },
  {
    path: "/markets/ae",
    group: "market",
    eyebrow: "UAE · EARLY ACCESS",
    title: "Procurement control for UAE multi-branch businesses.",
    description: "Early-access procurement software for UAE SMEs, clinics and multi-branch organisations managing approvals, supplier quotations and purchasing evidence.",
    thesis: "UAE content focuses on multi-branch authority, supplier comparisons, VAT-ready records and e-invoicing preparation while product localisation remains staged.",
    sections: [
      ["Multi-branch control", "Allow operating locations to initiate needs while responsible group teams retain approval and supplier visibility."],
      ["Record readiness", "Structure supplier, order, receipt and invoice evidence so finance teams can prepare for local accounting and e-invoicing processes."],
      ["Advisor channel", "Accounting firms, CFO advisors, tax consultants and ERP implementers can introduce appropriate organisations."],
    ],
    boundary: "RAQOZ does not currently claim UAE tax compliance, Federal Tax Authority validation, Peppol connectivity or native UAE accounting integration.",
    ctaLabel: "Join UAE early access",
    ctaHref: "mailto:cyclotrontechnologies@gmail.com?subject=RAQOZ%20UAE%20Early%20Access",
    taxonomy: taxonomy({ country: "ae", primaryKeyword: "procurement software UAE SMEs", supportingKeywords: ["purchase approval software UAE", "UAE e-invoicing procurement"], productCapability: "global workflow; UAE localisation staged", complianceReview: "requires-local-review", publicationState: "early-access", repurposingStatus: "country-review-required", cta: "Join UAE early access" }),
  },
  {
    path: "/markets/us",
    group: "market",
    eyebrow: "UNITED STATES · EARLY ACCESS",
    title: "Purchase control before transactions reach the accounting system.",
    description: "Early-access procurement software for US small and mid-sized businesses using email, spreadsheets and accounting platforms to manage purchasing.",
    thesis: "US content uses organization and vendor terminology and focuses on approval authority, segregation of duties, multi-location buying and QuickBooks-adjacent control.",
    sections: [
      ["Control before accounting", "Capture the request, approval and vendor decision before the bill arrives in the accounting workflow."],
      ["Segregation of duties", "Keep requesting, approving, purchasing, receiving and finance responsibilities explicit and attributable."],
      ["Advisor channel", "CPAs, fractional CFOs, QuickBooks ProAdvisors and bookkeepers can identify clients that have outgrown email approvals."],
    ],
    boundary: "RAQOZ does not currently claim US tax compliance, W-9 validation, SOC 2 certification or native QuickBooks integration.",
    ctaLabel: "Join US early access",
    ctaHref: "mailto:cyclotrontechnologies@gmail.com?subject=RAQOZ%20US%20Early%20Access",
    taxonomy: taxonomy({ country: "us", primaryKeyword: "procurement software US small business", supportingKeywords: ["purchase approval workflow QuickBooks", "procurement controls fractional CFO"], productCapability: "global workflow; US localisation staged", complianceReview: "requires-local-review", publicationState: "early-access", repurposingStatus: "country-review-required", cta: "Join US early access" }),
  },
  {
    path: "/markets/uk",
    group: "market",
    eyebrow: "UNITED KINGDOM · EARLY ACCESS",
    title: "Purchase approvals and evidence for growing UK businesses.",
    description: "Early-access procurement software for UK SMEs and multi-site organisations controlling purchase orders, receipts and invoice evidence.",
    thesis: "UK content focuses on accountable purchase approvals, multi-site operations, VAT-ready records and Xero-adjacent workflows while localisation remains staged.",
    sections: [
      ["Approval before commitment", "Keep the purchase request and authority attached before a supplier receives an order."],
      ["Evidence for finance", "Connect order, receipt and invoice differences so accounts teams can review the commercial record."],
      ["Advisor channel", "Accountants, fractional finance directors, Xero advisors and bookkeepers can identify recurring client-control gaps."],
    ],
    boundary: "RAQOZ does not currently claim UK tax or Making Tax Digital compliance, statutory advice or native Xero integration.",
    ctaLabel: "Join UK early access",
    ctaHref: "mailto:cyclotrontechnologies@gmail.com?subject=RAQOZ%20UK%20Early%20Access",
    taxonomy: taxonomy({ country: "uk", primaryKeyword: "procurement software UK SMEs", supportingKeywords: ["purchase approval workflow Xero", "procurement controls UK accountants"], productCapability: "global workflow; UK localisation staged", complianceReview: "requires-local-review", publicationState: "early-access", repurposingStatus: "country-review-required", cta: "Join UK early access" }),
  },
  {
    path: "/industries/healthcare",
    group: "industry",
    eyebrow: "HEALTHCARE",
    title: "Accountable procurement across clinics, laboratories and care locations.",
    description: "Healthcare procurement software for multi-location clinics, diagnostic laboratories and specialty-care businesses.",
    thesis: "Operating locations need speed while central teams need comparable suppliers, equipment evidence, warranty context and receipt visibility.",
    sections: [
      ["Clinical requirement context", "Keep specifications and technical review visible without turning procurement software into a clinical system."],
      ["Equipment and consumables", "Separate capital equipment, operating purchases, consumables and service obligations while retaining the purchase lineage."],
      ["Location accountability", "Record which location requested, received, owns and follows up the purchase."],
    ],
    links: [["Open the detailed healthcare pack", "/industries/healthcare-procurement"], ["Plan a new clinic location", "/tools/new-location-procurement-planner"]],
    boundary: "RAQOZ does not replace LIMS, HMS/EMR, patient billing, clinical inventory or healthcare regulatory systems.",
    ctaLabel: "Pilot one healthcare purchase",
    ctaHref: "/one-purchase-challenge",
    taxonomy: taxonomy({ industry: "healthcare", primaryKeyword: "healthcare procurement software", supportingKeywords: ["clinic procurement software", "laboratory purchasing"], productCapability: "multi-location purchasing and asset evidence", cta: "Pilot one healthcare purchase" }),
  },
  {
    path: "/industries/construction",
    group: "industry",
    eyebrow: "CONSTRUCTION & FIT-OUT",
    title: "Control indirect purchases and services across active sites.",
    description: "Procurement control for construction, real-estate and fit-out teams managing site requests, supplier quotations, orders and staged receipts.",
    thesis: "Project teams need fast site purchasing while leadership and finance need clear scope, supplier decisions, delivery and service evidence.",
    sections: [
      ["Site-originated needs", "Capture location, required date, specification and accountable project owner before sourcing."],
      ["Comparable commercial scope", "Normalise freight, installation, exclusions and milestone assumptions across supplier responses."],
      ["Staged outcome evidence", "Connect partial delivery, service acceptance and invoice evidence to the controlled order."],
    ],
    links: [["Open the fit-out procurement pack", "/industries/real-estate-fitout-procurement"], ["Use the service checklist", "/templates/service-procurement-checklist"]],
    boundary: "RAQOZ does not replace BOQ, tender, quantity-surveying, contractor measurement, design or project-scheduling systems.",
    ctaLabel: "Pilot one site purchase",
    ctaHref: "/one-purchase-challenge",
    taxonomy: taxonomy({ industry: "construction", primaryKeyword: "construction procurement software", supportingKeywords: ["site purchase approval", "fit out procurement"], productCapability: "site requests, sourcing and staged receipt", cta: "Pilot one site purchase" }),
  },
  {
    path: "/industries/hospitality",
    group: "industry",
    eyebrow: "HOSPITALITY & MULTI-SITE OPERATIONS",
    title: "Keep property purchasing fast without losing group control.",
    description: "Procurement software for hotels, hospitality groups and multi-property operators managing local requests and central supplier decisions.",
    thesis: "Properties buy operating supplies, equipment and services continuously. Group teams need policy and evidence without slowing urgent operating needs.",
    sections: [
      ["Property requests", "Let each property record the operational requirement, quantity, location and required date."],
      ["Group sourcing", "Compare recurring categories and supplier terms across properties instead of repeating isolated purchases."],
      ["Receipt and service evidence", "Keep delivery, installation, warranty and service acceptance attached to the order."],
    ],
    links: [["Open multi-site services pack", "/industries/multi-site-service-procurement"], ["Plan a new property", "/tools/new-location-procurement-planner"]],
    boundary: "RAQOZ does not replace hotel property-management, food inventory, accounting or facilities work-order systems.",
    ctaLabel: "Pilot one property purchase",
    ctaHref: "/one-purchase-challenge",
    taxonomy: taxonomy({ industry: "hospitality", primaryKeyword: "hospitality procurement software", supportingKeywords: ["hotel purchase approval", "multi property purchasing"], productCapability: "multi-location procurement workflow", cta: "Pilot one property purchase" }),
  },
  {
    path: "/industries/professional-services",
    group: "industry",
    eyebrow: "PROFESSIONAL SERVICES",
    title: "Control subscriptions, contractors and operating purchases.",
    description: "Procurement software for professional-services firms managing software, contractors, equipment and recurring supplier commitments.",
    thesis: "Professional-services buying often bypasses formal procurement because each individual purchase appears simple. The risk accumulates across subscriptions, renewals and distributed teams.",
    sections: [
      ["Structured intake", "Capture the business owner, purpose, term, users, data considerations and budget before commitment."],
      ["Commercial comparison", "Compare scope, term, renewal, support and exit conditions—not only monthly price."],
      ["Obligation visibility", "Keep renewals, notice dates, ownership and evidence connected to the original decision."],
    ],
    links: [["Open the AMC tracker", "/tools/amc-warranty-tracker"], ["Download service checklist", "/templates/service-procurement-checklist"]],
    boundary: "RAQOZ does not replace contract legal review, IT security assessment, professional-services delivery or accounting systems.",
    ctaLabel: "Pilot one service purchase",
    ctaHref: "/one-purchase-challenge",
    taxonomy: taxonomy({ industry: "professional services", primaryKeyword: "procurement software professional services", supportingKeywords: ["software purchase approval", "contractor procurement"], productCapability: "service procurement and obligation evidence", cta: "Pilot one service purchase" }),
  },
];

export function globalGrowthPath(path: string) {
  const clean = path.replace(/\/+$/, "") || "/";
  return globalGrowthPages.some((page) => page.path === clean);
}

export function globalGrowthPage(path: string) {
  const clean = path.replace(/\/+$/, "") || "/";
  return globalGrowthPages.find((page) => page.path === clean);
}
